import { AxiosError } from "axios";

import { SupportedDocumentQueries } from "../../payslips/services/model/FeatureConfiguration";
import PayslipQueryIcon from "../../../common/assets/payslip-query.svg";
import RosterQueryIcon from "../../../common/assets/roster-query.svg";
import TimesheetQueryIcon from "../../../common/assets/timesheet-query.svg";
import LeaveQueryIcon from "../../../common/assets/leave-query.svg";
import {
  InboxQueryDetails,
  InboxQueryStatus,
  InboxTabs,
  QueryCategory,
  QueryOption,
  QueryOutcome,
} from "./types";

const TERMINAL_STATUSES: InboxQueryStatus[] = [
  InboxQueryStatus.RESOLVED,
  InboxQueryStatus.APPROVED,
  InboxQueryStatus.DECLINED,
  InboxQueryStatus.CANCELLED,
];

export interface TicketActionVisibility {
  hidden: boolean;
  disabled: boolean;
}

/**
 * Decides whether the Approve/Decline controls on a leave/query ticket should be shown and enabled.
 *
 * A multi-level leave request stays PENDING while it moves up the approval chain, so ticket status
 * alone cannot tell whether it is still the viewing user's turn. When the backend reports the viewer
 * is not the current approver (`can_action === false`) we hide/disable the controls so a stale
 * button can't be clicked and 403 (ENG-5210). `can_action` is only acted on when explicitly `false`;
 * if it is absent (older backend) the existing status-based gating is preserved unchanged.
 */
export const getTicketActionVisibility = (
  ticket: Pick<InboxQueryDetails, "status" | "can_action">,
  inboxQueryTab: InboxTabs,
  isResolving: boolean
): TicketActionVisibility => {
  const isTerminal = TERMINAL_STATUSES.includes(ticket.status);
  const notCurrentApprover = ticket.can_action === false;

  const hidden = isTerminal || notCurrentApprover;
  const disabled =
    isTerminal ||
    (ticket.status === InboxQueryStatus.PENDING &&
      inboxQueryTab === InboxTabs.RESOLVED) ||
    notCurrentApprover ||
    isResolving;

  return { hidden, disabled };
};

/**
 * The monolith's resolve error code for a leave request that has already been actioned or advanced
 * to the next approver (django-monolith #945 / ENG-5230). Sent as `code` on the HTTP 409 body.
 */
export const TICKET_ALREADY_ACTIONED_CODE = "TICKET_ALREADY_ACTIONED";

/**
 * Friendly copy shown when a resolve fails because the request has already moved on. This is an
 * expected race (a co-approver got there first), not a fault, so it replaces the generic
 * resolve-error toast for this one case.
 */
export const TICKET_ALREADY_ACTIONED_MESSAGE =
  "This request has already moved to the next approver";

/**
 * True when a resolve failed specifically because the ticket has already been actioned/advanced,
 * signalled by the monolith as HTTP 409 with `code: "TICKET_ALREADY_ACTIONED"` (ETS PR #66,
 * django-monolith #945). Note this is intentionally narrower than the ENG-5210 "not the current
 * approver" case, which the backend still returns as a 403 — that keeps its existing handling.
 */
export const isTicketAlreadyActionedError = (
  error?: AxiosError<{ message?: string; code?: string }> | null
): boolean =>
  error?.response?.status === 409 &&
  error?.response?.data?.code === TICKET_ALREADY_ACTIONED_CODE;

export const getQueryIcon = (
  queryType: SupportedDocumentQueries | string,
  category: QueryCategory = QueryCategory.DOCUMENT
): string => {
  if (category === QueryCategory.LEAVE) {
    return LeaveQueryIcon;
  }

  const queryText = `${queryType} Query`;
  switch (queryText) {
    case SupportedDocumentQueries.PAYSLIP_QUERY:
      return PayslipQueryIcon;
    case SupportedDocumentQueries.ROSTER_QUERY:
      return RosterQueryIcon;
    case SupportedDocumentQueries.TIMESHEET_QUERY:
      return TimesheetQueryIcon;
    default:
      return PayslipQueryIcon;
  }
};

export const makeOptions = (outcomes: QueryOutcome[]): QueryOption[] =>
  outcomes
    .filter((outcome) => outcome.active) // Filter out inactive outcomes
    .sort((a, b) => a.order - b.order) // Sort by order
    .map((outcome) => ({
      value: outcome.title,
      label: outcome.title,
    }));
